/**
 * Verification API module — dashboard client for the fix-verification endpoints.
 *
 * Mirrors the extension's verifyApi (packages/extension/src/lib/api/verify.ts) but
 * adapted to the dashboard's fetchApi wrapper (Authorization + X-User-Id headers,
 * envelope unwrap). The dashboard's `source` for bulk job creation is always
 * `'dashboard_bulk'`.
 */
import type {
  IssueVerification,
  VerificationJob,
  VerifyRequestBody,
} from '@overlayqa/shared';
import { fetchApi } from './client';
import { log } from '../logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/verify] Module loaded');

// Response shape from POST /issues/verify-bulk.
export interface CreateBulkJobResponse {
  jobId: string;
  urlGroups: Record<string, string[]>;
  totalIssues: number;
}

// Response shape from GET /verifications/jobs/:jobId — the job row plus its
// progress entries and the corresponding verifications.
export interface GetJobResponse extends VerificationJob {
  progress: Array<{
    jobId: string;
    issueId: string;
    verificationId: string;
    completedAt: string;
  }>;
  verifications: IssueVerification[];
}

// Response shape from POST /verifications/jobs/:jobId/progress.
export interface RecordProgressResponse {
  ok: true;
  alreadyRecorded?: boolean;
}

// Generic ok envelope returned by pause/resume/cancel handlers.
export interface JobCommandResponse {
  ok: true;
}

// Response shape from GET /issues/:id/verifications.
export interface ListVerificationsResponse {
  verifications: IssueVerification[];
}

export const verifyApi = {
  // POST /issues/:id/verify — run a single verification synchronously.
  verifyIssue: (
    token: string | undefined,
    issueId: string,
    body: VerifyRequestBody
  ): Promise<IssueVerification> => {
    log.info('[api/verify] verifyIssue', {
      issueId,
      source: body.source,
      hasAxe: !!body.axeResults,
      hasTokens: !!body.tokenResults,
      jobId: body.jobId,
    });
    return fetchApi<IssueVerification>(`/issues/${issueId}/verify`, {
      token,
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // GET /issues/:id/verifications — fetch newest-first history (capped 50).
  listVerifications: (
    token: string | undefined,
    issueId: string
  ): Promise<ListVerificationsResponse> => {
    log.info('[api/verify] listVerifications', { issueId });
    return fetchApi<ListVerificationsResponse>(`/issues/${issueId}/verifications`, {
      token,
      timeoutMs: SCREEN_READ_TIMEOUT_MS,
    });
  },

  // POST /issues/verify-bulk — create a verification job for a set of issues
  // grouped by canonical page URL. The dashboard always sends source =
  // 'dashboard_bulk'.
  createBulkJob: (
    token: string | undefined,
    input: {
      issueIds: string[];
      source: 'dashboard_bulk';
      idempotencyKey: string;
    }
  ): Promise<CreateBulkJobResponse> => {
    log.info('[api/verify] createBulkJob', {
      issueCount: input.issueIds.length,
      source: input.source,
    });
    return fetchApi<CreateBulkJobResponse>('/issues/verify-bulk', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(input),
    });
  },

  // GET /verifications/jobs/:jobId — fetch job state, progress, and the
  // verifications that have been recorded against it so far.
  getJob: (token: string | undefined, jobId: string): Promise<GetJobResponse> => {
    log.info('[api/verify] getJob', { jobId });
    return fetchApi<GetJobResponse>(`/verifications/jobs/${jobId}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS });
  },

  // POST /verifications/jobs/:jobId/progress — record that a given issue was
  // verified within a bulk job. Idempotent on (jobId, issueId). The dashboard
  // typically does not call this directly (the extension is the verifier in
  // dashboard_bulk jobs), but it's exposed for completeness.
  recordProgress: (
    token: string | undefined,
    jobId: string,
    issueId: string,
    verificationId: string
  ): Promise<RecordProgressResponse> => {
    log.info('[api/verify] recordProgress', { jobId, issueId, verificationId });
    return fetchApi<RecordProgressResponse>(`/verifications/jobs/${jobId}/progress`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ issueId, verificationId }),
    });
  },

  // POST /verifications/jobs/:jobId/pause — pause a running/pending job.
  pauseJob: (token: string | undefined, jobId: string): Promise<JobCommandResponse> => {
    log.info('[api/verify] pauseJob', { jobId });
    return fetchApi<JobCommandResponse>(`/verifications/jobs/${jobId}/pause`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },

  // POST /verifications/jobs/:jobId/resume — resume a paused job.
  resumeJob: (token: string | undefined, jobId: string): Promise<JobCommandResponse> => {
    log.info('[api/verify] resumeJob', { jobId });
    return fetchApi<JobCommandResponse>(`/verifications/jobs/${jobId}/resume`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },

  // DELETE /verifications/jobs/:jobId — cancel a job (marks as cancelled,
  // sets cancelReason="user_cancelled", and stamps completedAt).
  cancelJob: (token: string | undefined, jobId: string): Promise<JobCommandResponse> => {
    log.info('[api/verify] cancelJob', { jobId });
    return fetchApi<JobCommandResponse>(`/verifications/jobs/${jobId}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },
};
